import axios from 'axios'
import { encryptCredentials } from '../utils/crypto'
import type {
    ImputacionMonth,
    ImputacionSummary,
    ProjectActivities,
    RowChoice,
    SubmitRequest,
    SubmitResponse,
    TeamImputacionStatus,
    TeamScope,
} from '../types/imputacion'
import type { CvExtractionState } from '../types/cv'

const API_BASE_URL = '/api/v1'

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

// Request interceptor to add auth token
axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Don't redirect if already on login page or if it's the /me check
            const isLoginPage = window.location.pathname === '/login'
            const isMeEndpoint = error.config?.url?.includes('/me')

            if (!isLoginPage && !isMeEndpoint) {
                localStorage.removeItem('auth_token')
                window.location.href = '/login'
            }
        }
        return Promise.reject(error)
    }
)

// Auth API
export const authApi = {
    login: (username: string, password: string) => {
        const value = encryptCredentials(username, password)
        return axiosInstance.post('/login', { value })
    },
    logout: () => axiosInstance.post('/logout'),
    me: () => axiosInstance.get('/me'),
}

// Users API
export const usersApi = {
    getAll: () => axiosInstance.get('/users'),
    getMatrix: () => axiosInstance.get('/users/matrix'),
    getById: (id: number) => axiosInstance.get(`/users/${id}`),
    getProfile: (id: number) => axiosInstance.get(`/users/${id}/profile`),
    updateProfile: (id: number, data: any) => axiosInstance.put(`/users/${id}/profile`, data),
    // (No create/update: POST /users and PUT /users/{id} don't exist; users come from login.)
    delete: (id: number) => axiosInstance.delete(`/users/${id}`),
    // Knowledge levels of any user (GET /categories only returns the session user's)
    getKnowledge: (id: number) =>
        axiosInstance.get<Array<{
            id: number
            category_group: string
            subcategory_name: string
            value: string
            last_modified_at: string | null
            last_modified_by: string | null
        }>>(`/users/${id}/knowledge`),
    // User opportunities (assigned projects)
    getOpportunities: (id: number) => axiosInstance.get(`/users/${id}/opportunities`),
    // Weekly load history
    getLoadHistory: (id: number, startDate?: string, endDate?: string) =>
        axiosInstance.get(`/users/${id}/load-history`, { params: { start_date: startDate, end_date: endDate } }),
    // CV operations
    // Also starts reading the CV in the background (`extraction` is that read)
    uploadCV: (id: number, file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return axiosInstance.post<{ message: string; filename: string; extraction: CvExtractionState | null }>(`/users/${id}/cv`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },
    getCV: (id: number) => axiosInstance.get(`/users/${id}/cv`, { responseType: 'blob' }),
    deleteCV: (id: number) => axiosInstance.delete(`/users/${id}/cv`),
    // The CV is read in the background on the server (20-40 s with Gemini); nothing is written
    // to the profile. POST starts or joins the read (a done one is reused), GET polls it.
    extractCV: (id: number) => axiosInstance.post<CvExtractionState>(`/users/${id}/cv/extract`),
    getCvExtraction: (id: number) => axiosInstance.get<CvExtractionState | null>(`/users/${id}/cv/extract`),
    // After a save that included the proposal, so it is not filled in again
    markCvExtractionApplied: (id: number) =>
        axiosInstance.post<CvExtractionState | null>(`/users/${id}/cv/extract/applied`),
    // Certificate file operations
    uploadCertificateFile: (userId: number, certIndex: number, file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return axiosInstance.post(`/users/${userId}/certificates/${certIndex}/file`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },
    getCertificateFile: (userId: number, certIndex: number) =>
        axiosInstance.get(`/users/${userId}/certificates/${certIndex}/file`, { responseType: 'blob' }),
    deleteCertificateFile: (userId: number, certIndex: number) =>
        axiosInstance.delete(`/users/${userId}/certificates/${certIndex}/file`),
    // Title file operations
    uploadTitleFile: (userId: number, titleIndex: number, file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return axiosInstance.post(`/users/${userId}/titles/${titleIndex}/file`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },
    getTitleFile: (userId: number, titleIndex: number) =>
        axiosInstance.get(`/users/${userId}/titles/${titleIndex}/file`, { responseType: 'blob' }),
    deleteTitleFile: (userId: number, titleIndex: number) =>
        axiosInstance.delete(`/users/${userId}/titles/${titleIndex}/file`),
    // Documents ZIP download
    getDocumentsZip: (userId: number) =>
        axiosInstance.get(`/users/${userId}/documents/zip`, { responseType: 'blob' }),
    getAllDocumentsZip: (docType?: string) =>
        axiosInstance.get('/users/documents/zip-all', { responseType: 'blob', params: docType ? { doc_type: docType } : {} }),
    // Prácticas (internship) operations
    getPracticasInfo: (userId: number) =>
        axiosInstance.get(`/users/${userId}/practicas`),
    updatePracticasInfo: (userId: number, data: {
        total_horas: number,
        horario: 'media_jornada' | 'jornada_completa',
        tipo_practicas: 'curriculares' | 'extracurriculares'
    }) => axiosInstance.put(`/users/${userId}/practicas`, data),
    toggleInfraAccess: (userId: number) =>
        axiosInstance.put(`/users/${userId}/infra-access`),
}

// Opportunities API
export const opportunitiesApi = {
    getAll: () => axiosInstance.get('/opportunities'),
    getAutocomplete: () => axiosInstance.get('/opportunities/autocomplete'),
    getById: (id: number) => axiosInstance.get(`/opportunities/${id}`),
    create: (data: any) => axiosInstance.post('/opportunities', data),
    update: (id: number, data: any) => axiosInstance.put(`/opportunities/${id}`, data),
    delete: (id: number) => axiosInstance.delete(`/opportunities/${id}`),
    // File operations
    uploadFile: (id: number, file: File, fileCategory: 'oferta' | 'rfp' = 'oferta') => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('file_category', fileCategory)
        return axiosInstance.post(`/opportunities/${id}/files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },
    getFiles: (id: number) => axiosInstance.get(`/opportunities/${id}/files`),
    downloadFile: (fileId: number) =>
        axiosInstance.get(`/opportunities/files/${fileId}/download`, { responseType: 'blob' }),
    deleteFile: (fileId: number) => axiosInstance.delete(`/opportunities/files/${fileId}`),
    getSummary: (id: number) => axiosInstance.get(`/opportunities/${id}/summary`),
    // Change log
    getChangeLog: (id: number) => axiosInstance.get(`/opportunities/${id}/changelog`),
    // SharePoint folder validation
    validateFolder: (id: number) => axiosInstance.get(`/opportunities/${id}/validate-folder`),
    // SharePoint sync — starts a background job and returns { job_id }
    syncSharepointPreview: () => axiosInstance.get('/opportunities/sync-sharepoint/preview'),
    syncSharepoint: (body?: { paths?: string[] }) =>
        axiosInstance.post('/opportunities/sync-sharepoint', body ?? {}),
    // Poll status of a background sync job
    syncSharepointStatus: (jobId: string) =>
        axiosInstance.get(`/opportunities/sync-sharepoint/status/${jobId}`),
    // Get the currently-running sync job (shared across all admins)
    syncSharepointCurrent: () => axiosInstance.get('/opportunities/sync-sharepoint/current'),
    // Cancel a running background sync
    syncSharepointCancel: (jobId: string) =>
        axiosInstance.post(`/opportunities/sync-sharepoint/${jobId}/cancel`),
    // Client-name deduplication helpers
    getSimilarClients: () => axiosInstance.get('/opportunities/similar-clients'),
    // `excluded`: names left out of a proposed group, remembered as different companies
    mergeClients: (canonical: string, oldNames: string[], excluded: string[] = []) =>
        axiosInstance.post('/opportunities/merge-clients', { canonical, old_names: oldNames, excluded }),
    // 'No son el mismo': these names are different companies, never proposed together again
    dismissSimilarClients: (names: string[]) =>
        axiosInstance.post('/opportunities/similar-clients/dismiss', { names }),
}

// Timeline API
export const timelineApi = {
    getProjects: (fromDate: string, toDate: string) =>
        axiosInstance.get(`/timeline/projects?from_date=${fromDate}&to_date=${toDate}`),
    getTeam: (fromDate: string, toDate: string) =>
        axiosInstance.get(`/timeline/team?from_date=${fromDate}&to_date=${toDate}`),
    exportProjects: (fromDate: string, toDate: string) =>
        axiosInstance.get(`/timeline/projects/export?from_date=${fromDate}&to_date=${toDate}`, {
            responseType: 'blob',
        }),
    exportTeam: (fromDate: string, toDate: string) =>
        axiosInstance.get(`/timeline/team/export?from_date=${fromDate}&to_date=${toDate}`, {
            responseType: 'blob',
        }),
}

// Dashboard/Stats API
export const dashboardApi = {
    getStats: () => axiosInstance.get('/dashboard/stats'),
    getStaleOffers: () => axiosInstance.get('/dashboard/stale-offers'),
    getResponsables: () => axiosInstance.get('/dashboard/responsables'),
    getStatsByResponsable: () => axiosInstance.get('/dashboard/stats/by-responsable'),
    getClientes: () => axiosInstance.get('/dashboard/clientes'),
    getStatsByCliente: () => axiosInstance.get('/dashboard/stats/by-cliente'),
    getStatsBySector: () => axiosInstance.get('/dashboard/stats/by-sector'),
    getStatsByTecnologia: () => axiosInstance.get('/dashboard/stats/by-tecnologia'),
    getKnowledgeMatrix: () => axiosInstance.get('/matrix'),
    updateCategoryKnowledge: (categoryId: number, value: string) =>
        axiosInstance.put(`/categories/${categoryId}`, { value }),
    updateUserKnowledge: (userId: number, categoryId: number, value: string) =>
        axiosInstance.put(`/users/${userId}/knowledge/${categoryId}`, { value }),
    getUserCategories: () => axiosInstance.get('/categories'),
}

// Config API
export const configApi = {
    getOpportunities: () => axiosInstance.get('/config/opportunities'),
    updateOpportunities: (data: any) => axiosInstance.put('/config/opportunities', data),
    getCategories: () => axiosInstance.get('/config/categories'),
    getRoles: () => axiosInstance.get('/config/roles'),
    getProjects: () => axiosInstance.get('/config/projects'),
    getGantt: () => axiosInstance.get('/config/gantt'),
    /** F-H3: backend-sourced enum registry. Returns
     *  { tipo_proyecto: [...], probabilidad: [...], ... } */
    getEnums: () => axiosInstance.get('/config/enums'),
}

// Admin API
export const adminApi = {
    getProfileStats: () => axiosInstance.get('/admin/profile-stats'),
    sendInitialNotifications: () => axiosInstance.post('/admin/send-initial-notifications'),
    sendReminderNotifications: (minCompletionPct: number = 75) =>
        axiosInstance.post(`/admin/send-reminder-notifications?min_completion_pct=${minCompletionPct}`),
    getTeamLoadHistory: (startDate?: string, endDate?: string, includeProjection?: boolean) =>
        axiosInstance.get('/admin/team-load-history', { params: { start_date: startDate, end_date: endDate, include_projection: includeProjection } }),
}

// Recommendations API
export interface RecommendationRequest {
    opportunity_id: number
    rol: string
    cargabilidad: number
    tecnologias?: string[]
    fecha_inicio?: string
    fecha_fin?: string
    /**
     * Role-level deferred-start offset. Always applied on top of the base
     * start date. Accepted values: 'Inmediato' | '1 mes' | '3 meses' | '6 meses'.
     * Defaults to 'Inmediato' on the backend.
     */
    start_availability?: string
    max_candidatos?: number
    incluir_practicas?: boolean  // Include interns in recommendations
}

export interface MatchMotivo {
    tipo: string
    descripcion: string
    valor?: string
    peso: number
}

export interface CurrentAssignment {
    opportunity_id: number
    opportunity_name: string
    cliente?: string | null
    rol?: string
    cargabilidad: number
    importe?: number
}

// Score breakdown types for explainability
export interface PenaltyComponents {
    experience_penalty: number
    role_penalty: number
    project_value_penalty: number
    tenure_penalty?: number
    tenure_months?: number
    total_penalty: number
    w_experience: number
    w_role: number
    w_project_value: number
    w_tenure_client?: number
}

export interface BonusComponents {
    sector_match_bonus: number
    certification_bonus: number
    technology_match_bonus: number
    role_match_bonus?: number
    client_history_bonus?: number
    client_history_count?: number
    total_bonus: number
}

export interface ScoreBreakdown {
    tier_value: number
    tier_name: string
    tier_score: number
    tier_contribution: number
    availability_pct: number
    availability_factor: number
    availability_contribution: number
    penalty_components: PenaltyComponents
    penalty_multiplier: number
    score_after_penalty: number
    bonus_components: BonusComponents
    overqualification_penalty?: number
    role_rank_gap?: number
    final_score: number
    formula_display: string
}

export interface CandidateRecommendation {
    user_id: number
    username: string
    display_name: string
    job_role?: string
    years_experience?: number
    tier: number
    tier_nombre: string
    score_base: number
    score_final: number
    score_breakdown?: ScoreBreakdown
    disponibilidad_actual: number
    disponibilidad_periodo: number
    overlap_pct: number
    penalizacion_reasignacion: number
    asignaciones_actuales: CurrentAssignment[]
    motivos: MatchMotivo[]
    tecnologias_coincidentes: string[]
    match_sector: boolean
    // recommendation_history row this suggestion was saved as (null when the
    // insert failed); the decision on "Asignar" is recorded on exactly this row.
    history_id?: number | null
}

export interface RecommendationResponse {
    opportunity_id: number
    opportunity_name: string
    rol_solicitado: string
    cargabilidad_solicitada: number
    fecha_inicio?: string
    fecha_fin?: string
    probabilidad_oferta?: string
    factor_probabilidad: number
    incertidumbre_z: number
    asignacion_efectiva: number
    candidatos: CandidateRecommendation[]
    candidatos_ocupados?: CandidateRecommendation[]
    total_candidatos: number
    // Hiring need / rebalancing detection fields
    necesita_contratacion: boolean
    rebalanceo_sugerido: boolean
    candidatos_sin_disponibilidad: number
    candidatos_con_perfil_parcial: number
    capacidad_insuficiente_mensaje?: string
    resumen_capacidad: {
        total_evaluados: number
        con_perfil_adecuado: number
        con_perfil_parcial: number
        al_100_cargabilidad: number
        disponibilidad_insuficiente: number
        candidatos_recomendados: number
        cargabilidad_solicitada_pct: number
        asignacion_efectiva_pct: number
        accion_sugerida: 'rebalanceo' | 'contratacion' | 'ninguna'
    }
    generated_at: string
    version?: number
}

export interface RecommendationVersion {
    version: number
    last_update: string | null
    affected_user_ids: number[]
    affected_opportunity_ids: number[]
    is_stale?: boolean
}

export interface HiringNeedRequest {
    fecha_desde: string
    fecha_hasta: string
    granularidad: 'mensual' | 'trimestral'
    rol?: string
    sector?: string
    incluir_overlaps?: boolean
}

export interface RolNecesidad {
    rol: string
    necesidad_personas: number
    ofertas_implicadas: any[]
}

export interface OverlapActivo {
    user_id: number
    username: string
    display_name: string
    cargabilidad_total: number
    overlap_pct: number
    proyectos: any[]
}

export interface PeriodoNecesidad {
    periodo_inicio: string
    periodo_fin: string
    periodo_label: string
    necesidad_total: number
    capacidad_disponible: number
    deficit: number
    por_rol: RolNecesidad[]
    overlaps_activos: OverlapActivo[]
}

export interface HiringNeedResponse {
    fecha_desde: string
    fecha_hasta: string
    granularidad: string
    resumen: {
        total_necesidad: number
        total_capacidad: number
        total_deficit: number
        num_periodos: number
        roles_top: [string, number][]
        num_ofertas_analizadas: number
        alerta: boolean
    }
    periodos: PeriodoNecesidad[]
    calculated_at: string
}

export interface RecommendationExplainRequest {
    candidate_name: string
    candidate_role?: string
    candidate_years?: number
    requested_role: string
    opportunity_name: string
    cliente?: string
    score_breakdown: ScoreBreakdown
    motivos: MatchMotivo[]
}

export interface RecommendationExplainResponse {
    summary: string
}

export const recommendationsApi = {
    // Get candidate recommendations
    getCandidates: (data: RecommendationRequest) =>
        axiosInstance.post<RecommendationResponse>('/recommendations/candidates', data),

    // Generate a natural-language explanation for a single candidate
    explain: (data: RecommendationExplainRequest) =>
        axiosInstance.post<RecommendationExplainResponse>('/recommendations/explain', data),

    // Quick recommendations with minimal params
    getQuickCandidates: (opportunityId: number, rol?: string, cargabilidad?: number) =>
        axiosInstance.get<RecommendationResponse>(
            `/recommendations/candidates/${opportunityId}`,
            { params: { rol: rol || '', cargabilidad: cargabilidad || 100 } }
        ),

    // Calculate hiring needs
    calculateHiringNeed: (data: HiringNeedRequest) =>
        axiosInstance.post<HiringNeedResponse>('/estadisticas/necesidad-contratacion', data),

    // Get hiring needs with defaults
    getHiringNeed: (params?: {
        fecha_desde?: string
        fecha_hasta?: string
        granularidad?: string
        rol?: string
        sector?: string
    }) => axiosInstance.get<HiringNeedResponse>('/estadisticas/necesidad-contratacion', { params }),

    // Recommendation history
    getHistory: (opportunityId?: number, userId?: number, limit?: number) =>
        axiosInstance.get('/recommendations/history', {
            params: { opportunity_id: opportunityId, user_id: userId, limit }
        }),

    // Record decision on recommendation
    recordDecision: (historyId: number, fueAsignado: boolean) =>
        axiosInstance.put(`/recommendations/history/${historyId}/decision`, null, {
            params: { fue_asignado: fueAsignado }
        }),

    // Get recommendation cache version for polling
    getVersion: (clientVersion?: number) =>
        axiosInstance.get<RecommendationVersion>('/recommendations/version', {
            params: clientVersion !== undefined ? { client_version: clientVersion } : {}
        }),
}

// Chatbot API
export interface Conversation {
    id: string
    title: string
    created_at: string
    updated_at: string
    message_count: number
}

export interface ChatMessage {
    id: number
    role: 'user' | 'assistant'
    content: string
    created_at: string
}

export const chatbotApi = {
    // Conversations
    createConversation: (title?: string) =>
        axiosInstance.post<Conversation>('/chatbot/conversations', { title: title || 'Nueva conversación' }),
    getConversations: () => axiosInstance.get<Conversation[]>('/chatbot/conversations'),
    deleteConversation: (id: string) => axiosInstance.delete(`/chatbot/conversations/${id}`),
    getMessages: (conversationId: string) =>
        axiosInstance.get<ChatMessage[]>(`/chatbot/conversations/${conversationId}/messages`),

    // Streaming message - returns EventSource URL
    getMessageStreamUrl: (conversationId: string) =>
        `/api/v1/chatbot/conversations/${conversationId}/messages`,

    // Send message with SSE streaming
    sendMessage: async (
        conversationId: string,
        message: string,
        onChunk: (chunk: string) => void,
        onDone: () => void,
        onError: (error: string) => void
    ): Promise<void> => {
        const response = await fetch(`/api/v1/chatbot/conversations/${conversationId}/messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
            },
            credentials: 'include',
            body: JSON.stringify({ message }),
        })

        if (!response.ok) {
            onError(`Error: ${response.status}`)
            return
        }

        const reader = response.body?.getReader()
        if (!reader) {
            onError('No reader available')
            return
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6))
                        if (data.chunk) {
                            onChunk(data.chunk)
                        } else if (data.done) {
                            onDone()
                        } else if (data.error) {
                            onError(data.error)
                        }
                    } catch {
                        // Ignore parsing errors
                    }
                }
            }
        }
    },

    // Edit last message with streaming response
    editLastMessage: async (
        conversationId: string,
        content: string,
        onChunk: (chunk: string) => void,
        onDone: () => void,
        onError: (error: string) => void
    ): Promise<void> => {
        const response = await fetch(`/api/v1/chatbot/conversations/${conversationId}/messages/last`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
            },
            credentials: 'include',
            body: JSON.stringify({ content }),
        })

        if (!response.ok) {
            onError(`Error: ${response.status}`)
            return
        }

        const reader = response.body?.getReader()
        if (!reader) {
            onError('No reader available')
            return
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6))
                        if (data.chunk) {
                            onChunk(data.chunk)
                        } else if (data.done) {
                            onDone()
                        } else if (data.error) {
                            onError(data.error)
                        }
                    } catch {
                        // Ignore parsing errors
                    }
                }
            }
        }
    },
}

// Billing / Imputación API
export const billingApi = {
    // Get monthly allocation table for all users (Admin only)
    getAllocations: (year: number, month: number) =>
        axiosInstance.get('/billing/allocations', { params: { year, month } }),
    // Get current user's monthly allocation
    getMyAllocations: (year: number, month: number) =>
        axiosInstance.get('/billing/allocations/me', { params: { year, month } }),
    // Get project hours alerts (Admin only)
    getAlerts: () =>
        axiosInstance.get('/billing/alerts'),
    // List all projects with Redmine billing codes
    getProjects: () =>
        axiosInstance.get('/billing/projects'),
    // Sync user allocations for a month (Admin only)
    syncAllocations: (year: number, month: number) =>
        axiosInstance.post('/billing/sync/allocations', null, { params: { year, month } }),
    // Sync Redmine hours data (Admin only)
    syncRedmineHours: () =>
        axiosInstance.post('/billing/sync/redmine'),
    // Get billing status per opportunity (hours consumed %, projection alerts) for a month
    // (default: the current one). The worst alert of the project or any of its subprojects wins.
    getOpportunityStatus: (year?: number, month?: number) =>
        axiosInstance.get('/billing/opportunity-status', { params: { year, month } }),
}

// Redmine integration API
export const redmineApi = {
    // Credential status (any user) / management (admin)
    getStatus: () => axiosInstance.get('/redmine/status'),
    getCredentials: () => axiosInstance.get('/redmine/credentials'),
    setCredentials: (data: { username: string; password?: string; api_key?: string }) =>
        axiosInstance.put('/redmine/credentials', data),
    deleteCredentials: () => axiosInstance.delete('/redmine/credentials'),
    testConnection: () => axiosInstance.post('/redmine/credentials/test'),
    // Instance reference data (trackers, statuses, custom fields...) — admin
    getReference: () => axiosInstance.get('/redmine/reference'),
    // Projects
    searchProjects: (q: string, limit = 30) =>
        axiosInstance.get('/redmine/projects', { params: { q, limit } }),
    getProjectOverview: (ref: string | number, timeFrom?: string) =>
        axiosInstance.get(`/redmine/projects/${encodeURIComponent(String(ref))}/overview`, {
            params: timeFrom ? { time_from: timeFrom } : undefined,
        }),
    getSubprojects: (ref: string | number) =>
        axiosInstance.get(`/redmine/projects/${encodeURIComponent(String(ref))}/subprojects`),
    getIssue: (id: number) => axiosInstance.get(`/redmine/issues/${id}`),
    search: (q: string, wiki = false) => axiosInstance.get('/redmine/search', { params: { q, wiki } }),
    // Opportunity link
    getOpportunity: (oppId: number, timeFrom?: string) =>
        axiosInstance.get(`/redmine/opportunities/${oppId}`, { params: timeFrom ? { time_from: timeFrom } : undefined }),
    // Who logs hours on the linked project vs the project team ('Alinear con Redmine')
    getTeamAlignment: (oppId: number) => axiosInstance.get(`/redmine/opportunities/${oppId}/team-alignment`),
    suggestProjects: (oppId: number, q = '', limit = 10) =>
        axiosInstance.get(`/redmine/opportunities/${oppId}/suggestions`, { params: { q, limit } }),
    linkOpportunity: (oppId: number, data: { project_id?: number; identifier?: string }) =>
        axiosInstance.put(`/redmine/opportunities/${oppId}/link`, data),
    unlinkOpportunity: (oppId: number) => axiosInstance.delete(`/redmine/opportunities/${oppId}/link`),
    // Users
    listUsers: () => axiosInstance.get('/redmine/users'),
    getUserMapping: () => axiosInstance.get('/redmine/users/mapping'),
    autoMapUsers: () => axiosInstance.post('/redmine/users/mapping/auto'),
    setUserMapping: (userId: number, data: { redmine_user_id: number | null; redmine_login?: string; redmine_name?: string }) =>
        axiosInstance.put(`/redmine/users/${userId}/mapping`, data),
    // Recommended vs logged time entries
    getTimeComparison: (year: number, month: number, userId?: number) =>
        axiosInstance.get('/redmine/time-entries/comparison', { params: { year, month, user_id: userId } }),
    getMyTimeComparison: (year: number, month: number) =>
        axiosInstance.get('/redmine/time-entries/comparison/me', { params: { year, month } }),
}

// Imputación: the person's own month (logging in Redmine with their own login)
// and the admin's read-only team views. Every /me route is exempt from the 401
// redirect above (its URL contains '/me'), so ImputacionPanel handles the
// expired session itself and keeps the unsent draft.
export const imputacionApi = {
    // year/month omitted → the server picks the month (the previous one on days 1-5 while it is still open)
    getMine: (year?: number, month?: number) =>
        axiosInstance.get<ImputacionMonth>('/imputacion/me', { params: { year, month } }),
    // Never touches Redmine: last own-login snapshot plus the calendar
    getMySummary: () => axiosInstance.get<ImputacionSummary>('/imputacion/me/summary'),
    getProjectActivities: (projectId: number) =>
        axiosInstance.get<ProjectActivities>(`/imputacion/me/projects/${projectId}/activities`),
    setRowChoice: (body: RowChoice) => axiosInstance.put<RowChoice>('/imputacion/me/row-choices', body),
    // One ISO week per call; 90 s so a slow Redmine is not cut short (the client re-reads on timeout, never resends)
    submit: (body: SubmitRequest) =>
        axiosInstance.post<SubmitResponse>('/imputacion/me/entries', body, { timeout: 90000 }),
    // Admin only
    getTeam: (year: number, month: number, scope: TeamScope = 'team', refresh = false) =>
        axiosInstance.get<TeamImputacionStatus>('/imputacion/team', { params: { year, month, scope, refresh } }),
    getUser: (userId: number, year?: number, month?: number) =>
        axiosInstance.get<ImputacionMonth>(`/imputacion/users/${userId}`, { params: { year, month } }),
}

// Infrastructure API
export const infrastructureApi = {
    getAll: () => axiosInstance.get('/infrastructure'),
    getById: (id: number) => axiosInstance.get(`/infrastructure/${id}`),
    create: (data: any) => axiosInstance.post('/infrastructure', data),
    update: (id: number, data: any) => axiosInstance.put(`/infrastructure/${id}`, data),
    delete: (id: number) => axiosInstance.delete(`/infrastructure/${id}`),
    // Partitions
    createPartition: (data: any) => axiosInstance.post('/infrastructure/partitions', data),
    updatePartition: (id: number, data: any) => axiosInstance.put(`/infrastructure/partitions/${id}`, data),
    deletePartition: (id: number) => axiosInstance.delete(`/infrastructure/partitions/${id}`),
    // Assignments
    createAssignment: (data: any) => axiosInstance.post('/infrastructure/assignments', data),
    updateAssignment: (id: number, data: any) => axiosInstance.put(`/infrastructure/assignments/${id}`, data),
    deleteAssignment: (id: number) => axiosInstance.delete(`/infrastructure/assignments/${id}`),
    // Refresh infrastructure (nvidia-smi MIG detection)
    refresh: () => axiosInstance.post('/infrastructure/refresh', {}, { timeout: 60000 }),
    // Options for opportunity dropdown. Pass the current opp_id so the
    // backend keeps this opportunity's own assignments visible (otherwise
    // the current selection would vanish from the dropdown on edit).
    getProjectOptions: (opportunityId?: number | null) => {
        const url = opportunityId
            ? `/infrastructure/options/for-projects?opportunity_id=${opportunityId}`
            : '/infrastructure/options/for-projects'
        return axiosInstance.get(url)
    },
    // Assignments by opportunity IDs
    getAssignmentsByOpportunities: (ids: number[]) =>
        axiosInstance.get(`/infrastructure/assignments/by-opportunities?ids=${ids.join(',')}`),
}

// LinkedIn API
// =============================================================================
// Field names are centralized here so backend contract drift is a one-file fix.

/** A LinkedIn post ingested by the bot pipeline. */
/** A post video: a playable URL (may be null for adaptive streams) + a poster frame. */
export interface PostVideo {
    url?: string | null
    poster?: string | null
}

export interface LinkedInPost {
    id: number
    linkedin_url: string
    post_urn?: string | null
    content?: string | null
    summary?: string | null
    category?: string | null
    tags?: string[] | null
    language?: string | null
    has_repo_link?: boolean
    has_paper_link?: boolean
    author?: string | null
    published_at?: string | null
    created_at?: string | null
    reactions?: number | null
    /** Media image URLs scraped from the post (LinkedIn CDN). */
    image_urls?: string[] | null
    /** Videos scraped from the post (playable url + poster). */
    video_urls?: PostVideo[] | null
    /** What the post carries, inline with the list row. `has_repo_link` only says
     *  *whether* there is a repo; these say which one, with the stars and the
     *  provenance the badges show. Empty arrays, never null. */
    repos?: RepoLite[]
    papers?: PaperLite[]
}

/** A GitHub (or other platform) repository detected from a post. */
export interface LinkedInRepo {
    id: number
    platform?: string | null
    owner: string
    name: string
    stars?: number | null
    forks?: number | null
    language?: string | null
    health_score?: number | null
    is_trending?: boolean
    topics?: string[] | null
    description?: string | null
    ai_description?: string | null
    /** False once GitHub reports the repo deleted or private. */
    is_active?: boolean | null
    sync_status?: string | null
    // Feed enrichment (active assignments)
    is_assigned?: boolean
    assigned_to?: number[]
    // Feed enrichment (associated publications)
    has_post: boolean
    post_count: number
    /** ...of which this many came from a URL the author actually wrote, in the
     *  post or in their own comment on it. The rest were inferred from a project
     *  name, and `post_count - linked_post_count` is the only thing separating a
     *  repo somebody linked on purpose from one we guessed. */
    linked_post_count?: number
    // Feed enrichment (associated papers)
    has_paper?: boolean
    paper_count?: number
    // Latest commit (from the repo agent's commit sync)
    commit_count_30d?: number | null
    last_commit_sha?: string | null
    last_commit_message?: string | null
    last_commit_at?: string | null
    last_commit_branch?: string | null
}

/** An academic paper detected from a post. */
export interface LinkedInPaper {
    id: number
    source?: string | null
    external_id?: string | null
    title: string
    url?: string | null
    repo_id?: number | null
    // Associated (backing) repository — papers are generally tied to a repo
    repo_platform?: string | null
    repo_owner?: string | null
    repo_name?: string | null
    // Feed enrichment (active assignments)
    is_assigned?: boolean
    assigned_to?: number[]
    created_at?: string | null
    // Feed enrichment (associated publications)
    has_post: boolean
    post_count: number
}

/** A single commit shown in a repo detail view (newest first). */
export interface LinkedInCommit {
    sha: string
    branch?: string | null
    message?: string | null
    committed_at?: string | null
    additions?: number | null
    deletions?: number | null
}

/** Lightweight post shape embedded inside repo/paper detail responses. */
export interface LinkedInPostLite {
    id: number
    linkedin_url: string
    post_urn?: string | null
    author?: string | null
    content?: string | null
    summary?: string | null
    category?: string | null
    tags?: string[] | null
    reactions?: number | null
    created_at?: string | null
    /** Media image URLs scraped from the post (LinkedIn CDN). */
    image_urls?: string[] | null
    /** Videos scraped from the post (playable url + poster). */
    video_urls?: PostVideo[] | null
}

/** Lightweight assignment shape embedded inside repo/paper detail responses. */
export interface LinkedInAssignmentLite {
    id: number
    user_id: number
    user_display_name?: string | null
    estado?: AssignmentEstado | null
    fecha_inicio?: string | null
    fecha_fin?: string | null
    progress_pct?: number | null
    /** The intern's own repo, as opposed to the public one being studied. */
    internal_repo_url?: string | null
}

/** Full repo detail (GET /linkedin/repos/{id}) including related entities. */
export interface RepoDetail extends LinkedInRepo {
    open_issues?: number | null
    contributors_count?: number | null
    is_active?: boolean
    is_paper_backed?: boolean
    has_citation?: boolean
    license?: string | null
    latest_release?: string | null
    default_branch?: string | null
    readme_excerpt?: string | null
    paper_url?: string | null
    dataset_links?: string[] | null
    sync_status?: string | null
    last_synced_at?: string | null
    commits?: LinkedInCommit[]
    papers?: PaperLite[]
    posts?: LinkedInPostLite[]
    assignments?: LinkedInAssignmentLite[]
}

/** Full paper detail (GET /linkedin/papers/{id}) including the backing repo. */
export interface PaperDetail extends LinkedInPaper {
    created_at?: string | null
    repo_ai_description?: string | null
    repo_stars?: number | null
    repo_health_score?: number | null
    posts?: LinkedInPostLite[]
    assignments?: LinkedInAssignmentLite[]
}

/**
 * How a repo or a paper got attached to a post.
 *
 * 'link'         — the author wrote the URL in the post itself.
 * 'comment_link' — the author wrote it in their own comment on the post, which is
 *                  where LinkedIn's reach penalty pushes most repo announcements.
 *                  Still the author's own claim, but attached to this post by
 *                  matching the commenter's identity to the author's — a match
 *                  that can misfire, which a body URL cannot.
 * 'name_search'  — nobody wrote a URL and the repo was inferred from a project
 *                  name written in the post.
 *
 * The three are deliberately kept apart all the way from the database, so the UI
 * must not fold them back together: a reader has to be able to tell a fact the
 * author stated from something we deduced.
 */
export type MentionType = 'link' | 'comment_link' | 'name_search'

/** Lightweight repo shape embedded inside the post detail response. */
export interface RepoLite {
    id: number
    platform?: string | null
    owner: string
    name: string
    stars?: number | null
    language?: string | null
    health_score?: number | null
    is_trending?: boolean
    ai_description?: string | null
    /** A property of the mention, not of the repo. Absent means the attribution
     *  is not qualified. */
    mention_type?: MentionType | string | null
    /** False once GitHub reports the repo deleted or private; `sync_status`
     *  becomes 'gone'. The repo list hides those, but a post's own repos cannot
     *  be hidden — they are why the post is in the feed — so the card has to say
     *  so rather than render a dead link as a live project. */
    is_active?: boolean | null
    sync_status?: string | null
}

/** Lightweight paper shape embedded inside post/repo detail responses. */
export interface PaperLite {
    id: number
    source?: string | null
    external_id?: string | null
    title: string
    url?: string | null
    /** Same provenance the repos carry, and for the same reason — see
     *  `MentionType`. Only responses that join the mention table carry it. */
    mention_type?: MentionType | string | null
}

/** Full post detail (GET /linkedin/posts/{id}) including related repos/papers. */
export interface PostDetail extends LinkedInPost {
    repos?: RepoLite[]
    papers?: PaperLite[]
    /** Who shared this post with the bot, from the DM it arrived in. */
    shared_by?: { sender_name?: string | null; sender_profile_url?: string | null; shared_at?: string | null }[]
}

/** A semantic cluster of posts. Mirrors the SELECT in feed_repository.list_clusters. */
export interface LinkedInCluster {
    id: number
    name: string
    slug: string
    keywords?: string[] | null
    post_count: number
    repo_count: number
}

/** A SOTA project: a bundle of repos, papers, posts and technologies. */
export interface LinkedInProjectSummary {
    id: number
    name: string
    description?: string | null
    technologies: string[]
    redmine_project_id?: number | null
    redmine_identifier?: string | null
    redmine_project_name?: string | null
    /** Set on the prácticas project of an intern. */
    user_id?: number | null
    repo_count: number
    paper_count: number
    post_count: number
    active_assignments: number
    created_by?: string | null
    created_at?: string | null
    updated_at?: string | null
}

export interface LinkedInProjectDetail extends LinkedInProjectSummary {
    repos: Array<{
        id: number
        platform?: string | null
        owner: string
        name: string
        stars?: number | null
        language?: string | null
        description?: string | null
        url?: string | null
    }>
    papers: Array<{ id: number; source?: string | null; external_id?: string | null; title?: string | null; url: string }>
    posts: Array<{
        id: number
        author?: string | null
        summary?: string | null
        category?: string | null
        linkedin_url?: string | null
        published_at?: string | null
    }>
    assignments: LinkedInAssignment[]
}

/** What the create / update calls take. On update, a list that is sent REPLACES the stored one. */
export interface LinkedInProjectInput {
    name?: string
    description?: string | null
    technologies?: string[]
    repo_ids?: number[]
    paper_ids?: number[]
    post_ids?: number[]
    /** 0 on update removes the Redmine link. */
    redmine_project_id?: number | null
}

/** One embedded post on the 2D cluster map (x, y in [-1, 1], unit-free). */
export interface LinkedInClusterMapPoint {
    post_id: number
    x: number
    y: number
    cluster_id: number | null
    similarity?: number | null
    author?: string | null
    summary?: string | null
    category?: string | null
    reactions?: number | null
    relevance_score?: number | null
    has_repo: boolean
    has_paper: boolean
    published_at?: string | null
    linkedin_url?: string | null
}

export interface LinkedInClusterMapCluster {
    id: number
    name: string
    slug: string
    keywords?: string[] | null
    /** Posts of this cluster on the map. */
    points: number
    /** Mean position of its posts, where to put the label; null when it has none on the map. */
    centroid: { x: number; y: number } | null
}

export interface LinkedInClusterMap {
    available: boolean
    message?: string | null
    points: LinkedInClusterMapPoint[]
    clusters: LinkedInClusterMapCluster[]
    unclustered_points?: number
    /** Share of the embeddings' variance each axis keeps. */
    explained_variance: number[]
}

/** One job's last run, from the linkedin_sync_run ledger. */
export interface LinkedInSyncJob {
    job: string
    started_at?: string | null
    finished_at?: string | null
    status: string
    items_processed?: number | null
    items_failed?: number | null
    error?: string | null
    /** Null when the job has never run: there is no elapsed time to report. */
    hours_since_last_run: number | null
    /** True when the last run is older than this job's cadence allows. */
    stale: boolean
    healthy: boolean
}

export interface LinkedInSyncStatus {
    jobs: LinkedInSyncJob[]
    healthy: boolean
}

/** Headline counts for the dashboard header. */
export interface LinkedInStats {
    posts: number
    posts_processed: number
    repos: number
    repos_trending: number
    papers: number
    commits: number
    senders: number
    open_assignments: number
    by_category: { category: string; n: number }[]
    by_language: { language: string; n: number }[]
}

export interface LinkedInTimeseries {
    metric: string
    days: number
    points: { day: string; n: number }[]
}

/** One daily snapshot of a repo's popularity and activity. */
export interface LinkedInRepoSnapshot {
    snapshot_date: string
    stars?: number | null
    forks?: number | null
    commit_count_30d?: number | null
    is_trending?: boolean | null
}

export interface LinkedInTopMover {
    id: number
    platform: string
    owner: string
    name: string
    language?: string | null
    is_trending?: boolean | null
    /** 0-100. Movement without it ranks a fast-growing unmaintained repo first. */
    health_score?: number | null
    value_from?: number | null
    value_to?: number | null
    delta: number
}

/** A post inside a cluster, with how representative it is. */
export interface LinkedInClusterPost {
    id: number
    linkedin_url?: string | null
    author?: string | null
    summary?: string | null
    category?: string | null
    tags?: string[] | null
    reactions?: number | null
    relevance_score?: number | null
    published_at?: string | null
    similarity?: number | null
}

export interface LinkedInClusterDetail extends LinkedInCluster {
    created_at?: string | null
    posts: LinkedInClusterPost[]
}

/** A post related to another, with the score and why. */
export interface LinkedInSimilarPost {
    id: number
    linkedin_url?: string | null
    author?: string | null
    summary?: string | null
    category?: string | null
    tags?: string[] | null
    reactions?: number | null
    score?: number | null
    /** 'same_repo' when they share a repository, 'ai_semantic' otherwise. */
    reason?: string | null
}

/** A research note left on a repo. */
export interface LinkedInRepoComment {
    id: number
    repo_id: number
    user_id?: number | null
    body: string
    created_at: string
    updated_at?: string | null
    user_display_name?: string | null
    username?: string | null
}

export type AssignmentTargetType = 'repo' | 'paper' | 'project'
export type AssignmentEstado = 'asignada' | 'en_progreso' | 'completada' | 'cancelada'

/** An assignment of a repo or paper to an intern as a learning task. */
export interface LinkedInAssignment {
    id: number
    user_id: number
    target_type: AssignmentTargetType
    target_id: number
    fecha_inicio: string
    fecha_fin?: string | null
    capacity_pct?: number | null
    estado?: AssignmentEstado | null
    progress_pct?: number | null
    notes?: string | null
    assigned_by?: string | null
    assigned_at?: string | null
    // Convenience fields the backend may include for display
    user_display_name?: string | null
    target_title?: string | null
    /** The intern's own repo, as opposed to the public one being studied. */
    internal_repo_url?: string | null
}

export interface LinkedInAssignmentCreate {
    user_id: number
    target_type: AssignmentTargetType
    target_id: number
    fecha_inicio: string
    fecha_fin?: string | null
    capacity_pct?: number
    estado?: AssignmentEstado
    notes?: string | null
    /** The intern's own repo, as opposed to the public one being studied. */
    internal_repo_url?: string | null
}

/** Fields PUT /assignments/{id} accepts. Not a Partial of Create: progress_pct is
 *  updatable but not settable at creation, and it was unreachable from the UI
 *  while this type was derived from Create. An explicit null clears a field. */
export interface LinkedInAssignmentUpdate {
    // No user_id/target_*: the assignee and target are fixed once created and
    // the backend rejects them on update (they used to be dropped silently).
    fecha_inicio?: string
    fecha_fin?: string | null
    capacity_pct?: number
    progress_pct?: number | null
    estado?: AssignmentEstado
    notes?: string | null
    internal_repo_url?: string | null
}

export interface LinkedInFacets {
    categories: string[]
    tags: string[]
    languages: string[]
    sources: string[]
}

/** 'signal' orders posts by the relevance_score the post filter computes. */
export type FeedSort = 'recent' | 'stars' | 'trending' | 'signal'

/** Filters for the feed endpoints. Booleans that are false should be omitted. */
export interface FeedFilters {
    search?: string
    category?: string
    tag?: string
    language?: string
    source?: string
    date_from?: string
    date_to?: string
    has_repo?: boolean
    has_paper?: boolean
    /** Papers only: whether any post mentions the paper. The repo agent creates
     *  papers out of a repo's README with no post at all, and this is the only
     *  way to enumerate that class. */
    has_post?: boolean
    trending?: boolean
    min_stars?: number
    /** Repos only: include the ones GitHub has deleted or made private. */
    include_inactive?: boolean
    sort?: FeedSort
    limit?: number
    offset?: number
}

export interface FeedListResponse<T> {
    items: T[]
    total: number
    limit: number
    offset: number
}

/**
 * Strip params that should not be sent: undefined, null and empty strings.
 *
 * `false` IS sent. The backend distinguishes absent (no filter) from false
 * (`has_repo_link = false`), and dropping false here made "posts with neither
 * repo nor paper" unaskable from the UI. Callers must therefore pass
 * `undefined`, never `false`, to mean "no filter".
 */
const cleanFeedParams = (filters: FeedFilters = {}): Record<string, any> => {
    const params: Record<string, any> = {}
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        if (typeof value === 'string' && value.trim() === '') return
        params[key] = value
    })
    return params
}

/** What the SOTA export can produce: everything (one sheet per kind) or one list. */
export type SotaExportEntity = 'all' | 'posts' | 'repos' | 'papers' | 'clusters' | 'assignments' | 'notes'
export type SotaExportFormat = 'xlsx' | 'csv'

export interface SotaExportRequest {
    entity: SotaExportEntity
    format: SotaExportFormat
    /** Only with entity posts/repos/papers: the list's filters. Pagination is
     *  dropped — the export carries every matching row, not the page on screen. */
    filters?: FeedFilters
}

/** Query string for /linkedin/export. */
export const sotaExportParams = ({ entity, format, filters }: SotaExportRequest): Record<string, any> => {
    const params = cleanFeedParams(filters)
    delete params.limit
    delete params.offset
    return { ...params, entity, format }
}

export const linkedinApi = {
    // Export: the file for the page's button. The chatbot asks
    // /linkedin/export/link instead, which answers with a download URL.
    exportSota: (request: SotaExportRequest) =>
        axiosInstance.get<Blob>('/linkedin/export', { params: sotaExportParams(request), responseType: 'blob' }),

    // Feeds
    getPosts: (filters: FeedFilters = {}) =>
        axiosInstance.get<FeedListResponse<LinkedInPost>>('/linkedin/posts', { params: cleanFeedParams(filters) }),
    getRepos: (filters: FeedFilters = {}) =>
        axiosInstance.get<FeedListResponse<LinkedInRepo>>('/linkedin/repos', { params: cleanFeedParams(filters) }),
    getPapers: (filters: FeedFilters = {}) =>
        axiosInstance.get<FeedListResponse<LinkedInPaper>>('/linkedin/papers', { params: cleanFeedParams(filters) }),
    // Detail (full record + related entities)
    getPost: (id: number) => axiosInstance.get<PostDetail>('/linkedin/posts/' + id),
    getRepo: (id: number) => axiosInstance.get<RepoDetail>('/linkedin/repos/' + id),
    getPaper: (id: number) => axiosInstance.get<PaperDetail>('/linkedin/papers/' + id),
    getClusters: () => axiosInstance.get<LinkedInCluster[]>('/linkedin/clusters'),
    getCluster: (id: number) => axiosInstance.get<LinkedInClusterDetail>('/linkedin/clusters/' + id),
    // SOTA projects
    getProjects: (search?: string) =>
        axiosInstance.get<LinkedInProjectSummary[]>('/linkedin/projects', { params: search ? { search } : {} }),
    getProject: (id: number) => axiosInstance.get<LinkedInProjectDetail>('/linkedin/projects/' + id),
    createProject: (data: LinkedInProjectInput & { name: string }) =>
        axiosInstance.post<LinkedInProjectDetail>('/linkedin/projects', data),
    createPracticasProject: (data: LinkedInProjectInput & { user_id: number }) =>
        axiosInstance.post<LinkedInProjectDetail>('/linkedin/projects/practicas', data),
    updateProject: (id: number, data: LinkedInProjectInput) =>
        axiosInstance.put<LinkedInProjectDetail>('/linkedin/projects/' + id, data),
    deleteProject: (id: number) => axiosInstance.delete<{ deleted: boolean; id: number }>('/linkedin/projects/' + id),
    getClusterMap: (refresh = false) =>
        axiosInstance.get<LinkedInClusterMap>('/linkedin/clusters/map', { params: refresh ? { refresh: true } : {} }),
    getSimilarPosts: (id: number, limit = 10) =>
        axiosInstance.get<LinkedInSimilarPost[]>(`/linkedin/posts/${id}/similar`, { params: { limit } }),
    getFacets: () => axiosInstance.get<LinkedInFacets>('/linkedin/facets'),

    // Pipeline health + dashboard
    getSyncStatus: () => axiosInstance.get<LinkedInSyncStatus>('/linkedin/sync-status'),
    getStats: () => axiosInstance.get<LinkedInStats>('/linkedin/stats'),
    getTimeseries: (metric: 'posts' | 'commits' | 'repos' = 'posts', days = 30) =>
        axiosInstance.get<LinkedInTimeseries>('/linkedin/timeseries', { params: { metric, days } }),

    // Repo history
    getRepoTimeseries: (id: number, days = 90) =>
        axiosInstance.get<{ repo_id: number; days: number; points: LinkedInRepoSnapshot[] }>(
            `/linkedin/repos/${id}/timeseries`,
            { params: { days } },
        ),
    getTopMovers: (metric: 'stars' | 'forks' | 'commit_count_30d' = 'stars', days = 7, limit = 10) =>
        axiosInstance.get<{ metric: string; days: number; items: LinkedInTopMover[] }>('/linkedin/repos/top-movers', {
            params: { metric, days, limit },
        }),

    // Research notes on a repo
    getRepoComments: (repoId: number) =>
        axiosInstance.get<LinkedInRepoComment[]>(`/linkedin/repos/${repoId}/comments`),
    createRepoComment: (repoId: number, body: string) =>
        axiosInstance.post<LinkedInRepoComment>(`/linkedin/repos/${repoId}/comments`, { body }),
    deleteRepoComment: (repoId: number, commentId: number) =>
        axiosInstance.delete(`/linkedin/repos/${repoId}/comments/${commentId}`),

    // Assignments
    getAssignments: () => axiosInstance.get<LinkedInAssignment[]>('/linkedin/assignments'),
    getAssignmentsByUser: (userId: number) =>
        axiosInstance.get<LinkedInAssignment[]>(`/linkedin/assignments/by-user/${userId}`),
    getAssignmentsByTarget: (targetType: AssignmentTargetType, targetId: number) =>
        axiosInstance.get<LinkedInAssignment[]>('/linkedin/assignments/by-target', {
            params: { target_type: targetType, target_id: targetId },
        }),
    createAssignment: (data: LinkedInAssignmentCreate) =>
        axiosInstance.post<LinkedInAssignment>('/linkedin/assignments', data),
    updateAssignment: (id: number, data: LinkedInAssignmentUpdate) =>
        axiosInstance.put<LinkedInAssignment>(`/linkedin/assignments/${id}`, data),
    closeAssignment: (id: number, fechaFin?: string) =>
        axiosInstance.post<LinkedInAssignment>(`/linkedin/assignments/${id}/close`, fechaFin ? { fecha_fin: fechaFin } : {}),
}

// Unified API object for easier imports
export const api = {
    auth: authApi,
    users: usersApi,
    opportunities: opportunitiesApi,
    timeline: timelineApi,
    dashboard: dashboardApi,
    config: configApi,
    chatbot: chatbotApi,
    admin: adminApi,
    recommendations: recommendationsApi,
    billing: billingApi,
    redmine: redmineApi,
    imputacion: imputacionApi,
    infrastructure: infrastructureApi,
    linkedin: linkedinApi,
}

export default api
